/**
 * Missions and tasks — "Görevler": the work itself, as records.
 *
 * A mission breaks an objective into tasks; every task completes on evidence.
 * This screen is where an operator creates both, moves them, and starts a
 * session for a task that is ready. What an operator may do to a task is read
 * off the task: the server sends the legal next states from the machine and
 * the decision endpoint maps a verdict onto one, so this file never carries a
 * copy of the transition table (tasksModel.ts explains the one lookup it does).
 *
 * Two columns on a wide screen, one under the other on a narrow one: the
 * missions as cards to pick from, and the tasks of the picked mission — or
 * every open task when none is picked — as a table with the decisions on each
 * row. A task opened by its route (`/tasks/:taskId`) gets a detail drawer.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { Plus, Terminal, X } from 'lucide-react'

import { api, type PageResponse } from '@/api/client'
import { Icon } from '@/components/icon'
import {
  Badge,
  Button,
  EmptyState,
  KeyValue,
  Page,
  PageHeader,
  Panel,
  StatusDot,
} from '@/components/primitives'
import {
  Dialog,
  Notice,
  QueryState,
  SelectField,
  TextAreaField,
  TextField,
  useApiErrorMessage,
} from '@/components/controls'
import { Toolbar } from '@/components/controls'
import { OverflowItem, OverflowMenu } from '@/components/menu'
import { useConfirm } from '@/components/confirm'
import { useT, useLocale } from '@/i18n/context'
import { formatRelative, hasMessage } from '@/i18n'
import { missionStateTone, riskTone, taskStateTone } from '@/lib/tone'
import { sessionActivityTone } from '@/lib/tone'
import { activityLabelKey, sessionHasEnded } from '@/lib/sessionActivity'
import { sessionLabel, useSessionActivity, useSessionsList, type SessionRow } from '@/terminal/wall'
import type {
  EvidenceCheck,
  ID,
  MissionResponse,
  MissionState,
  RouteCandidate,
  TaskArchetype,
  TaskArtifact,
  TaskResponse,
  TaskState,
} from '@/types/domain'
import { StartSessionDialog } from './StartSessionDialog'
import { ConductorPanel } from './ConductorPanel'
import {
  useMissions,
  useMissionTasks,
  useOpenTasks,
  useSimulateRoute,
  useTask,
  useOpenApprovals,
  useTaskEvidence,
  useWorkMutations,
} from './workData'
import { ApprovalCard } from './ApprovalCard'
import {
  TASK_BANDS,
  canStartSession,
  decisionsFor,
  matchesQuery,
  sessionsForTask,
  sortTasksForBoard,
  summariseTasks,
  taskBand,
  type TaskBand,
  type TaskDecision,
} from './tasksModel'
import './TasksPage.css'

const ARCHETYPES: TaskArchetype[] = [
  'backend',
  'frontend',
  'testing',
  'review',
  'research',
  'planning',
  'architecture',
  'mobile',
  'database',
  'devops',
  'security',
  'product',
  'design',
  'marketing',
  'advertising',
  'content',
  'analytics',
]

const RISKS = [
  'L0_READ_ONLY',
  'L1_ISOLATED_WRITE',
  'L2_VALIDATED_CHANGE',
  'L3_EXTERNAL_OR_IRREVERSIBLE',
  'L4_FINANCIAL_OR_SECURITY',
] as const

/** Verdicts that are audited with a reason and are worth a second look. */
const CONFIRMED_DECISIONS: TaskDecision[] = ['approve', 'rework', 'cancel']

/**
 * A state, an archetype or a decision in words — or, failing that, honestly.
 *
 * These three are interpolated into a key rather than switched, because the
 * server's enums are long and the catalogue is generated from them. That is a
 * trade with a cost: `t()` returns the key when there is no string for it, so a
 * value this build has never heard of — a state added to the machine after this
 * bundle shipped, a fixture with a typo — renders on screen as the literal text
 * `work.taskState.BLOCKED`. It has done exactly that.
 *
 * `hasMessage` is the narrow gate the rejection labels below already use. When
 * the string is missing, the *wire value* is shown instead: it is a machine
 * fact, it is what an operator would quote, and it is true. A missing-key
 * placeholder is not — it says nothing except that the console is broken.
 */
function labelFor(key: string, wire: string, t: ReturnType<typeof useT>): string {
  return hasMessage(key) ? t(key) : wire
}

function missionStateKey(state: MissionState) {
  return `work.missionState.${state}` as const
}
function taskStateKey(state: TaskState) {
  return `work.taskState.${state}` as const
}
function archetypeKey(a: TaskArchetype) {
  return `work.archetype.${a}` as const
}
/** A band in words. Switched rather than interpolated, so a band added to the
 *  model without a string is a compile error rather than a key printed on the
 *  screen — the same rule `actionLabel` keeps in the chief's control. */
function bandKey(b: TaskBand) {
  switch (b) {
    case 'NEEDS_PERSON':
      return 'work.band.needsPerson' as const
    case 'READY':
      return 'work.band.ready' as const
    case 'RUNNING':
      return 'work.band.running' as const
    case 'FAILED':
      return 'work.band.failed' as const
    case 'OTHER':
      return 'work.band.other' as const
  }
}

function bandTone(b: TaskBand) {
  switch (b) {
    case 'NEEDS_PERSON':
      return 'warn' as const
    case 'READY':
      return 'info' as const
    case 'RUNNING':
      return 'accent' as const
    case 'FAILED':
      return 'err' as const
    case 'OTHER':
      return 'neutral' as const
  }
}

function decisionKey(d: TaskDecision) {
  return `work.decision.${d}` as const
}

interface ProjectLite {
  id: ID
  name: string
  state: string
}

export function TasksPage() {
  const t = useT()
  const { locale } = useLocale()
  const navigate = useNavigate()
  const describe = useApiErrorMessage()
  const confirm = useConfirm()
  const { taskId } = useParams<{ taskId?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const missionFilter = searchParams.get('mission') ?? ''

  const missions = useMissions()
  const openTasks = useOpenTasks()
  const missionTasks = useMissionTasks(missionFilter || undefined)
  const sessions = useSessionsList()
  const activityOf = useSessionActivity(sessions)
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<PageResponse<ProjectLite>>('/projects'),
  })
  const { createMission, setMissionState, createTask, decide } = useWorkMutations()

  const [band, setBand] = useState<TaskBand | 'ALL'>('ALL')
  const [query, setQuery] = useState('')
  const [creatingMission, setCreatingMission] = useState(false)
  const [creatingTask, setCreatingTask] = useState(false)
  const [pendingDecision, setPendingDecision] = useState<{ task: TaskResponse; decision: TaskDecision } | null>(null)
  const [startingFor, setStartingFor] = useState<TaskResponse | null>(null)

  const missionList = useMemo(() => missions.data?.items ?? [], [missions.data])
  const selectedMission = missionList.find((m) => m.id === missionFilter)
  // A name, or nothing. It used to fall back to the id, so every card on a
  // screen whose project list had not arrived yet wore a uuid where a name goes.
  const projectName = (id: ID) => projects.data?.items.find((p) => p.id === id)?.name ?? ''
  const sessionRows = useMemo(() => sessions.data?.items ?? [], [sessions.data])

  const tasksQuery = missionFilter ? missionTasks : openTasks
  const tasks = useMemo(
    () => sortTasksForBoard(tasksQuery.data?.items ?? []),
    [tasksQuery.data],
  )
  const bandCounts = useMemo(() => summariseTasks(tasks), [tasks])
  const needsPerson = bandCounts.NEEDS_PERSON
  // The rows actually listed: the band chip, then what an operator typed. They
  // search on what somebody knows about a task — its title, its objective, the
  // mission it belongs to — not on its id, which nobody types.
  const visible = useMemo(
    () =>
      tasks.filter(
        (task) =>
          (band === 'ALL' || taskBand(task.state) === band) &&
          matchesQuery(
            [
              task.title,
              task.objective,
              missionList.find((m) => m.id === task.missionId)?.title,
            ],
            query,
          ),
      ),
    [tasks, band, query, missionList],
  )

  const selectMission = (id: string) => {
    const next = new URLSearchParams(searchParams)
    if (id) next.set('mission', id)
    else next.delete('mission')
    setSearchParams(next, { replace: true })
  }

  // A decision is sent as the operator's verdict; the server maps it onto the
  // machine. The ones with consequences ask for a reason first.
  const runDecision = async (task: TaskResponse, decision: TaskDecision) => {
    if (CONFIRMED_DECISIONS.includes(decision)) {
      setPendingDecision({ task, decision })
      return
    }
    decide.mutate({ id: task.id, decision })
  }

  const mutationError = createMission.error ?? setMissionState.error ?? createTask.error ?? decide.error

  return (
    <Page
      header={
        <PageHeader
          title={t('work.title')}
          /* A live count, which is what the subtitle slot holds on every other
             screen — the fleet says "7 çevrimiçi · 7 CLI eski", the board says
             "11 oturum · 3 ilgi bekliyor". This one said "a mission breaks an
             objective into tasks; every task completes on evidence", which is
             true, and is the screen advertising itself instead of reporting. */
          subtitle={
            missionList.length === 0 && tasks.length === 0
              ? undefined
              : needsPerson > 0
                ? t('work.count.attention', {
                    missions: missionList.length,
                    tasks: tasks.length,
                    attention: needsPerson,
                  })
                : t('work.count.calm', { missions: missionList.length, tasks: tasks.length })
          }
          actions={
            <Button variant="primary" size="sm" onClick={() => setCreatingMission(true)}>
              <Icon icon={Plus} size={15} />
              {t('work.createMission')}
            </Button>
          }
        />
      }
    >
      <div className="k-work">
        {mutationError && <Notice tone="err">{describe(mutationError).title}</Notice>}

        {/* ---- Missions ---- */}
        <Panel title={t('work.missions')} className="k-work__missions">
          <QueryState
            pending={missions.isPending}
            error={missions.error}
            onRetry={() => void missions.refetch()}
            empty={missionList.length === 0}
            emptyTitle={t('work.mission.none')}
            emptyHint={t('work.mission.noneHint')}
            emptyAction={
              <Button variant="primary" size="sm" onClick={() => setCreatingMission(true)}>
                <Icon icon={Plus} size={15} />
                {t('work.createMission')}
              </Button>
            }
          >
            <p className="k-work__hint">{t('work.mission.select')}</p>
            {/* `auto-fill`, not the `Grid` primitive's `auto-fit`. auto-fit
                collapses the empty tracks and shares the width out among the
                cards that exist, so three missions on a 3840 display became
                three twelve-hundred-pixel cards holding one short title each.
                auto-fill keeps a card the size of a card and leaves the rest of
                the row empty, which is what a row of cards is supposed to do. */}
            <div className="k-work__mgrid">
              {missionList.map((m) => (
                <MissionCard
                  key={m.id}
                  mission={m}
                  projectName={projectName(m.projectId)}
                  selected={m.id === missionFilter}
                  onSelect={() => selectMission(m.id === missionFilter ? '' : m.id)}
                  onMove={(state) => {
                    void (async () => {
                      if (state === 'CANCELLED' || state === 'FAILED') {
                        const ok = await confirm({
                          title: t('work.mission.move'),
                          message: t('work.decision.confirm', {
                            decision: labelFor(missionStateKey(state), state, t),
                          }),
                          confirmLabel: labelFor(missionStateKey(state), state, t),
                        })
                        if (!ok) return
                      }
                      setMissionState.mutate({ id: m.id, state, version: m.version })
                    })()
                  }}
                />
              ))}
            </div>
          </QueryState>
        </Panel>

        {/* ---- The conductor's batch planner: plan starts for ready tasks ---- */}
        <ConductorPanel taskTitle={(id) => (missionTasks.data?.items ?? openTasks.data?.items ?? []).find((x) => x.id === id)?.title ?? id} />

        {/* ---- Tasks ---- */}
        <Panel
          title={
            selectedMission
              ? `${t('work.tasks')} · ${selectedMission.title}`
              : t('work.allOpenTasks')
          }
          className="k-work__tasks"
          actions={
            <>
              {selectedMission && (
                <button
                  type="button"
                  className="k-work__chip"
                  onClick={() => selectMission('')}
                  aria-label={t('work.mission.clear')}
                  title={t('work.mission.clear')}
                >
                  {t('work.mission.selected', { title: selectedMission.title })}
                  <Icon icon={X} size={12} />
                </button>
              )}
              <Button
                size="sm"
                variant="primary"
                disabled={!selectedMission || selectedMission.nextStates.length === 0}
                title={selectedMission ? undefined : t('work.mission.select')}
                onClick={() => setCreatingTask(true)}
              >
                <Icon icon={Plus} size={15} />
                {t('work.createTask')}
              </Button>
            </>
          }
        >
          {/* The tally, and a way to find one task among fifty. Both were absent
              on the one screen that lists every open task in the system — the
              board next door has had them since it had eleven rows. The chips
              select the *band* the list is already ordered by (tasksModel), so
              a chip and the order under it cannot describe different things. */}
          {tasks.length > 0 && (
            <Toolbar
              actions={
                query !== '' || band !== 'ALL' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setQuery('')
                      setBand('ALL')
                    }}
                  >
                    {t('common.clearFilters')}
                  </Button>
                ) : undefined
              }
            >
              <div className="k-chips">
                <button
                  type="button"
                  className={clsx('k-chip', band === 'ALL' && 'k-chip--on')}
                  aria-pressed={band === 'ALL'}
                  onClick={() => setBand('ALL')}
                >
                  <span>{t('work.band.all')}</span>
                  <span className="k-chip__n k-mono">{tasks.length}</span>
                </button>
                {TASK_BANDS.filter((b) => bandCounts[b] > 0).map((b) => (
                  <button
                    key={b}
                    type="button"
                    className={clsx('k-chip', band === b && 'k-chip--on')}
                    aria-pressed={band === b}
                    onClick={() => setBand(band === b ? 'ALL' : b)}
                  >
                    <StatusDot tone={bandTone(b)} />
                    <span>{t(bandKey(b))}</span>
                    <span className="k-chip__n k-mono">{bandCounts[b]}</span>
                  </button>
                ))}
              </div>
              <TextField
                label={t('work.search')}
                labelHidden
                type="search"
                value={query}
                onChange={setQuery}
                placeholder={t('work.searchPlaceholder')}
              />
            </Toolbar>
          )}

          <QueryState
            pending={tasksQuery.isPending}
            error={tasksQuery.error}
            onRetry={() => void tasksQuery.refetch()}
            empty={tasks.length === 0}
            emptyTitle={selectedMission ? t('work.task.none') : t('work.task.noneOpen')}
            emptyHint={t('work.task.noneHint')}
            emptyAction={
              selectedMission ? (
                <Button variant="primary" size="sm" onClick={() => setCreatingTask(true)}>
                  <Icon icon={Plus} size={15} />
                  {t('work.createTask')}
                </Button>
              ) : undefined
            }
          >
            {visible.length === 0 ? (
              /* Its own words, not a second button called the same thing as
                 the one in the toolbar above it: two controls with one
                 accessible name in one region is a screen reader hearing the
                 same command twice with no way to tell them apart. */
              <EmptyState
                title={t('work.task.noMatch')}
                action={
                  <Button
                    size="sm"
                    onClick={() => {
                      setQuery('')
                      setBand('ALL')
                    }}
                  >
                    {t('work.task.showAll')}
                  </Button>
                }
              />
            ) : (
              /* No scroll container. `min-width: 56rem` on a box with
                 `overflow-x: auto` meant this table scrolled sideways inside
                 its panel on anything under about 900px — a phone got a table
                 to drag rather than the labelled cards `k-table--responsive`
                 has always been able to give it. */
              <table className="k-table k-table--responsive k-work__table">
                <caption className="k-visually-hidden">{t('work.allOpenTasks')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('work.task.state')}</th>
                    <th scope="col">{t('work.task.title')}</th>
                    <th scope="col">{t('work.task.archetype')}</th>
                    <th scope="col">{t('mission.priority')}</th>
                    <th scope="col">{t('work.task.session')}</th>
                    <th scope="col">{t('work.task.moved')}</th>
                    <th scope="col">
                      <span className="k-visually-hidden">{t('work.task.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((task) => {
                    const linked = sessionsForTask(task, sessionRows)
                    const live = linked.find((s) => !sessionHasEnded(s.state))
                    const decisions = decisionsFor(task)
                    const startable = canStartSession(task, sessionRows)
                    return (
                      <tr
                        key={task.id}
                        className={clsx('k-work__row', `k-work__row--${bandTone(taskBand(task.state))}`)}
                      >
                        {/* The cell is a table cell; the stack inside it is a
                            span. `display: flex` on a `<td>` takes it out of the
                            table layout altogether — the column algorithm stops
                            seeing it — which is why the state badge stretched
                            across half the row and the title dropped onto a line
                            of its own with every column after it shifted left. */}
                        <td data-label={t('work.task.state')}>
                          <span className="k-work__state">
                            <Badge tone={taskStateTone(task.state)} subtle title={task.state}>
                              {labelFor(taskStateKey(task.state), task.state, t)}
                            </Badge>
                            {task.blockedReason && (
                              <span className="k-work__blocked" title={task.blockedReason}>
                                {t('work.task.blocked', { reason: task.blockedReason })}
                              </span>
                            )}
                          </span>
                        </td>
                        <td data-label={t('work.task.title')} className="k-td--strong">
                          <span className="k-work__title">
                            <Link to={`/tasks/${task.id}?${searchParams.toString()}`} className="k-work__link">
                              {task.title}
                            </Link>
                            {!missionFilter && (
                              <span className="k-work__mission">
                                {missionList.find((m) => m.id === task.missionId)?.title ?? ''}
                              </span>
                            )}
                          </span>
                        </td>
                        <td data-label={t('work.task.archetype')}>
                          {labelFor(archetypeKey(task.archetype), task.archetype, t)}
                        </td>
                        <td data-label={t('mission.priority')} className="k-mono">
                          {task.priority}
                        </td>
                        <td data-label={t('work.task.session')} className="k-work__session">
                          {live ? (
                            <Link to={`/live/${live.id}`} className="k-work__link">
                              <Badge tone={sessionActivityTone(activityOf(live))} subtle>
                                {t(activityLabelKey(activityOf(live)))}
                              </Badge>{' '}
                              {sessionLabel(live, live.id)}
                            </Link>
                          ) : linked[0] ? (
                            <Link to={`/live/${linked[0].id}`} className="k-work__link k-work__link--muted">
                              {sessionLabel(linked[0], linked[0].id)} · {t('work.task.sessionEnded')}
                            </Link>
                          ) : (
                            <span className="k-work__muted" title={t('work.task.noSession')}>
                              —
                            </span>
                          )}
                        </td>
                        {/* How long it has sat there. A task list with no time
                            on it cannot answer the question an operator actually
                            has about a queue — which of these has been waiting,
                            and for how long. */}
                        <td data-label={t('work.task.moved')} className="k-work__moved k-mono">
                          {formatRelative(task.updatedAt, locale)}
                        </td>
                        <td data-label={t('work.task.actions')} className="k-work__actions">
                          {/* One element, not two loose children: below the table
                              breakpoint every cell becomes a label-and-value
                              grid, and a second child lands in the *label*
                              column — the kebab dropped onto a line of its own
                              under the label rather than beside the button. */}
                          <span className="k-work__actionbar">
                            {startable && (
                              <Button size="sm" variant="primary" onClick={() => setStartingFor(task)}>
                                <Icon icon={Terminal} size={14} />
                                {t('work.task.startSession')}
                              </Button>
                            )}
                            {decisions.length > 0 && (
                              <OverflowMenu label={t('work.task.actions')}>
                                {decisions.map((d) => (
                                  <OverflowItem key={d} onClick={() => void runDecision(task, d)}>
                                    {t(decisionKey(d))}
                                  </OverflowItem>
                                ))}
                              </OverflowMenu>
                            )}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </QueryState>
        </Panel>
      </div>

      {taskId && (
        <TaskDrawer
          taskId={taskId}
          sessions={sessionRows}
          missions={missionList}
          projectName={projectName}
          onClose={() => navigate(`/tasks${missionFilter ? `?mission=${missionFilter}` : ''}`)}
          onDecide={(task, d) => void runDecision(task, d)}
          onStart={(task) => setStartingFor(task)}
        />
      )}

      {creatingMission && (
        <CreateMissionDialog
          projects={(projects.data?.items ?? []).filter((p) => p.state !== 'ARCHIVED')}
          busy={createMission.isPending}
          error={createMission.error}
          onClose={() => setCreatingMission(false)}
          onSubmit={(input) =>
            createMission.mutate(input, {
              onSuccess: (m) => {
                setCreatingMission(false)
                selectMission(m.id)
              },
            })
          }
        />
      )}

      {creatingTask && selectedMission && (
        <CreateTaskDialog
          mission={selectedMission}
          siblings={tasks}
          busy={createTask.isPending}
          error={createTask.error}
          onClose={() => setCreatingTask(false)}
          onSubmit={(input) =>
            createTask.mutate(
              { missionId: selectedMission.id, input },
              { onSuccess: () => setCreatingTask(false) },
            )
          }
        />
      )}

      {pendingDecision && (
        <DecisionDialog
          task={pendingDecision.task}
          decision={pendingDecision.decision}
          busy={decide.isPending}
          error={decide.error}
          onClose={() => setPendingDecision(null)}
          onSubmit={(reason) =>
            decide.mutate(
              { id: pendingDecision.task.id, decision: pendingDecision.decision, reason },
              { onSuccess: () => setPendingDecision(null) },
            )
          }
        />
      )}

      {startingFor && (
        <StartSessionDialog
          task={{ id: startingFor.id, title: startingFor.title, projectId: startingFor.projectId }}
          onClose={() => setStartingFor(null)}
          onStarted={(id) => {
            setStartingFor(null)
            navigate(`/live/${id}`)
          }}
        />
      )}
    </Page>
  )
}

/* ------------------------------------------------------------ Mission card */

function MissionCard({
  mission,
  projectName,
  selected,
  onSelect,
  onMove,
}: {
  mission: MissionResponse
  projectName: string
  selected: boolean
  onSelect: () => void
  onMove: (state: MissionState) => void
}) {
  const t = useT()
  const c = mission.taskCounts
  return (
    <div className={clsx('k-work__mcard', selected && 'k-work__mcard--on')}>
      <button type="button" className="k-work__mpick" aria-pressed={selected} onClick={onSelect}>
        <span className="k-work__mtitle">{mission.title}</span>
        <span className="k-work__mmeta">
          <Badge tone={missionStateTone(mission.state)} subtle title={mission.state}>
            {labelFor(missionStateKey(mission.state), mission.state, t)}
          </Badge>
          {/* In words. `L2_VALIDATED_CHANGE` is the wire value and belongs in
              a `title`; on a card it is jargon where a sentence fits, and the
              strings for it have been in the catalogue all along — the projects
              screen has rendered them this way since it had cards. */}
          <Badge tone={riskTone(mission.riskLevel)} subtle title={mission.riskLevel}>
            {labelFor(`risk.${mission.riskLevel}`, mission.riskLevel, t)}
          </Badge>
          {projectName ? (
            <span className="k-work__mproject">{projectName}</span>
          ) : (
            /* Not a value. The project list had not arrived, or does not carry
               this one; a raw uuid on a card is the console showing its
               plumbing, and unmeasured has its own mark. */
            <span className="k-work__mproject k-work__muted" title={t('common.unmeasured')}>
              —
            </span>
          )}
        </span>
        <span className="k-work__mcounts k-mono">
          {t('work.mission.counts', { total: c.total, running: c.running, needsHuman: c.needsHuman })}
        </span>
      </button>
      {mission.nextStates.length > 0 && (
        <div className="k-work__mmenu">
          <OverflowMenu label={t('work.mission.move')}>
            {mission.nextStates.map((state) => (
              <OverflowItem key={state} onClick={() => onMove(state)}>
                {labelFor(missionStateKey(state), state, t)}
              </OverflowItem>
            ))}
          </OverflowMenu>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------ Task drawer */

function TaskDrawer({
  taskId,
  sessions,
  missions,
  projectName,
  onClose,
  onDecide,
  onStart,
}: {
  taskId: string
  sessions: SessionRow[]
  missions: MissionResponse[]
  projectName: (id: ID) => string
  onClose: () => void
  onDecide: (task: TaskResponse, d: TaskDecision) => void
  onStart: (task: TaskResponse) => void
}) {
  const t = useT()
  const { locale } = useLocale()
  const task = useTask(taskId)
  const d = task.data
  const contract = d?.evidenceContract
  const requirements = contract
    ? [
        contract.requireTestsPass && t('work.task.evidence.tests'),
        contract.requireBuildPass && t('work.task.evidence.build'),
        contract.requireLintPass && t('work.task.evidence.lint'),
        contract.requireDiff && t('work.task.evidence.diff'),
        contract.requireReviewer && t('work.task.evidence.reviewer'),
        contract.requireSources && t('work.task.evidence.sources'),
        contract.requireScreenshot && t('work.task.evidence.screenshot'),
        contract.requireHumanApproval && t('work.task.evidence.approval'),
        ...(contract.requiredArtifactTypes ?? []),
      ].filter((x): x is string => Boolean(x))
    : []
  const linked = d ? sessionsForTask(d, sessions) : []
  return (
    <Panel
      className="k-work__drawer"
      title={d?.title ?? t('work.task.open')}
      actions={
        <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('action.close')}>
          <Icon icon={X} size={15} />
        </Button>
      }
    >
      <QueryState pending={task.isPending} error={task.error} onRetry={() => void task.refetch()}>
        {d && (
          <>
            <div className="k-work__drawerstatus">
              <Badge tone={taskStateTone(d.state)} title={d.state}>
                {labelFor(taskStateKey(d.state), d.state, t)}
              </Badge>
              {canStartSession(d, sessions) && (
                <Button size="sm" variant="primary" onClick={() => onStart(d)}>
                  <Icon icon={Terminal} size={14} />
                  {t('work.task.startSession')}
                </Button>
              )}
              {decisionsFor(d).map((dec) => (
                <Button key={dec} size="sm" onClick={() => onDecide(d, dec)}>
                  {t(decisionKey(dec))}
                </Button>
              ))}
            </div>
            {d.blockedReason && <Notice tone="warn">{t('work.task.blocked', { reason: d.blockedReason })}</Notice>}
            <KeyValue
              items={[
                { key: 'mission', label: t('work.missions'), value: missions.find((m) => m.id === d.missionId)?.title ?? <span className="k-mono">{d.missionId}</span> },
                {
                  key: 'project',
                  label: t('work.mission.project'),
                  value: projectName(d.projectId) || (
                    <span className="k-work__muted" title={t('common.unmeasured')}>
                      —
                    </span>
                  ),
                },
                { key: 'objective', label: t('work.task.objective'), value: d.objective },
                {
                  key: 'archetype',
                  label: t('work.task.archetype'),
                  value: labelFor(archetypeKey(d.archetype), d.archetype, t),
                },
                { key: 'priority', label: t('mission.priority'), value: String(d.priority), mono: true },
                {
                  key: 'risk',
                  label: t('work.mission.risk'),
                  // The detail panel is where the wire value earns its place —
                  // it is what an operator quotes in a ticket — but it says what
                  // it means first.
                  value: (
                    <>
                      {labelFor(`risk.${d.riskLevel}`, d.riskLevel, t)}{' '}
                      <span className="k-mono k-work__muted">{d.riskLevel}</span>
                    </>
                  ),
                },
                {
                  key: 'attempts',
                  label: t('task.attempts'),
                  value: t('work.task.attempts', { count: d.attemptCount, max: d.maxAttempts }),
                  mono: true,
                },
                {
                  key: 'deps',
                  label: t('task.dependencies'),
                  value: t('work.task.dependencies', { count: d.dependsOn?.length ?? 0 }),
                },
                {
                  key: 'acceptance',
                  label: t('mission.acceptance'),
                  value:
                    d.acceptanceCriteria && d.acceptanceCriteria.length > 0 ? (
                      <ul className="k-work__list">
                        {d.acceptanceCriteria.map((c) => (
                          <li key={c}>{c}</li>
                        ))}
                      </ul>
                    ) : (
                      '—'
                    ),
                },
                {
                  key: 'evidence',
                  label: t('work.task.evidence'),
                  value:
                    requirements.length > 0 ? (
                      <ul className="k-work__list">
                        {requirements.map((r) => (
                          <li key={r}>{r}</li>
                        ))}
                      </ul>
                    ) : (
                      t('work.task.evidenceNone')
                    ),
                  hint: t('work.task.evidenceHint'),
                },
                {
                  key: 'sessions',
                  label: t('work.task.session'),
                  value:
                    linked.length > 0 ? (
                      <ul className="k-work__list">
                        {linked.map((s) => (
                          <li key={s.id}>
                            <Link to={`/live/${s.id}`} className="k-work__link">
                              {sessionLabel(s, s.id)}
                            </Link>{' '}
                            <span className="k-mono k-work__muted">
                              {s.state}
                              {s.endedAt ? ` · ${formatRelative(s.endedAt, locale)}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      t('work.task.noSession')
                    ),
                },
                {
                  key: 'next',
                  label: t('work.task.nextStates'),
                  value: d.nextStates.length > 0 ? d.nextStates.join(', ') : '—',
                  mono: true,
                },
                { key: 'id', label: 'id', value: d.id, mono: true },
              ]}
            />
            {/* The two halves of one gate, side by side: what the machine
                checked, and what a person has to sign off (ADR-0024). */}
            <TaskEvidencePanel taskId={taskId} state={d.state} />
            <TaskApproval taskId={taskId} state={d.state} />
            <RoutePreview taskId={taskId} />
          </>
        )}
      </QueryState>
    </Panel>
  )
}

/* -------------------------------------------------------------- Approval */

// TaskApproval shows the open decision for a task that stopped for a person.
//
// It asks only for a task in APPROVAL_REQUIRED: an approval that has been
// settled is history, and the drawer already says what the task's state is.
function TaskApproval({ taskId, state }: { taskId: string; state: TaskState }) {
  const open = useOpenApprovals()
  if (state !== 'APPROVAL_REQUIRED') return null
  const mine = open.data?.items?.find((a) => a.taskId === taskId)
  if (!mine) return null
  return <ApprovalCard taskId={taskId} approvalId={mine.id} />
}

/* ----------------------------------------------------------- Route preview */

// A short label for a rejection reason. The router's reasons are a stable wire
// contract, but — like a server-composed risk kind — a later build may add one,
// so an unknown reason falls back to its humanised code rather than a bare key.
function rejectionLabel(t: ReturnType<typeof useT>, reason: string): string {
  const key = `work.route.reject.${reason}`
  return hasMessage(key) ? t(key) : reason.replaceAll('_', ' ').toLowerCase()
}

// RoutePreview answers "why this account?" (ADR-0019): on demand it asks the
// router where this task would run and shows the account it would pick, the
// reason, and every account it rejected with why. It starts nothing — a dry run.
function RoutePreview({ taskId }: { taskId: string }) {
  const t = useT()
  const describe = useApiErrorMessage()
  const simulate = useSimulateRoute()
  const decision = simulate.data

  return (
    <div className="k-route">
      <div className="k-route__head">
        <span className="k-label">{t('work.route.title')}</span>
        <Button size="sm" variant="default" busy={simulate.isPending} onClick={() => simulate.mutate({ taskId })}>
          {t('work.route.preview')}
        </Button>
      </div>

      {simulate.error && <Notice tone="err">{describe(simulate.error).title}</Notice>}

      {decision && (
        <>
          {decision.selected ? (
            <p className="k-route__pick">
              <StatusDot tone="ok" label="selected" />
              {decision.selected.accountLabel || decision.selected.provider}
              <span className="k-mono k-route__score">{decision.selected.score.toFixed(2)}</span>
            </p>
          ) : (
            <p className="k-route__none">{t('work.route.none')}</p>
          )}
          {decision.explanation && <p className="k-route__why">{decision.explanation}</p>}

          {decision.candidates.filter((c) => !c.eligible).length > 0 && (
            <ul className="k-route__cands">
              {decision.candidates
                .filter((c: RouteCandidate) => !c.eligible)
                .map((c: RouteCandidate) => (
                  <li key={`${c.accountProfileId}-${c.modelId ?? ''}`} className="k-route__cand">
                    <StatusDot tone="err" label="rejected" />
                    <span className="k-route__acct">{c.accountLabel || c.provider}</span>
                    <span className="k-route__reasons">
                      {(c.rejectionReasons ?? []).map((rr) => rejectionLabel(t, rr)).join(', ')}
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

/* --------------------------------------------------------- Evidence panel */

const EVIDENCE_STATES: TaskState[] = ['REVIEWING', 'APPROVAL_REQUIRED', 'COMPLETED', 'REWORK_REQUIRED']

/** requirementLabel maps a check's stable key to a translated label, falling
 *  back to the raw key for one a later build added. */
function requirementLabel(t: ReturnType<typeof useT>, requirement: string): string {
  switch (requirement) {
    case 'diff':
      return t('work.evidence.check.diff')
    case 'tests':
      return t('work.evidence.check.tests')
    case 'build':
      return t('work.evidence.check.build')
    case 'lint':
      return t('work.evidence.check.lint')
    case 'forbidden_paths':
      return t('work.evidence.check.forbiddenPaths')
    default:
      return requirement
  }
}

// TaskEvidencePanel shows what Kimene collected for a task and the machine check
// of it against the contract: the diff, and the verdict of the operator's own
// build/test/lint (ADR-0018). The check is the machine half of the completion
// requirement — a person still approves — so the panel says what is clear and
// what is not, and the approve button the server offers is informed by it.
function TaskEvidencePanel({ taskId, state }: { taskId: string; state: TaskState }) {
  const t = useT()
  // Collection only happens once a task reaches review; before that there is
  // nothing to fetch, and asking would only 200 with an empty set.
  const relevant = EVIDENCE_STATES.includes(state)
  const evidence = useTaskEvidence(taskId, relevant)
  if (!relevant) return null

  const data = evidence.data
  const checks = data?.checks ?? []
  const artifacts = data?.artifacts ?? []
  const hasAny = data && (artifacts.length > 0 || checks.length > 0)

  return (
    <div className="k-evidence">
      <div className="k-evidence__head">
        <span className="k-label">{t('work.evidence.title')}</span>
        {data &&
          (data.satisfied ? (
            <Badge tone="ok" subtle>
              {t('work.evidence.satisfied')}
            </Badge>
          ) : hasAny ? (
            <Badge tone="warn" subtle>
              {t('work.evidence.unsatisfied')}
            </Badge>
          ) : null)}
      </div>

      <QueryState pending={evidence.isPending} error={evidence.error} onRetry={() => void evidence.refetch()}>
        {!hasAny ? (
          <p className="k-evidence__empty">{t('work.evidence.none')}</p>
        ) : (
          <>
            {checks.length > 0 && (
              <ul className="k-evidence__checks">
                {checks.map((c: EvidenceCheck) => (
                  <li key={c.requirement} className="k-evidence__check">
                    <StatusDot tone={c.satisfied ? 'ok' : 'err'} label={c.satisfied ? 'ok' : 'fail'} />
                    <span className="k-evidence__req">{requirementLabel(t, c.requirement)}</span>
                    {c.detail && <span className="k-evidence__detail">{c.detail}</span>}
                  </li>
                ))}
              </ul>
            )}
            {artifacts.length > 0 && (
              <ul className="k-evidence__arts">
                {artifacts.map((a: TaskArtifact) => (
                  <li key={a.id} className={clsx('k-evidence__art', a.superseded && 'k-evidence__art--old')}>
                    <span className="k-mono k-evidence__type">{a.type}</span>
                    {a.passed != null && (
                      <StatusDot tone={a.passed ? 'ok' : 'err'} label={a.passed ? 'passed' : 'failed'} />
                    )}
                    <span className="k-evidence__summary">{a.summary ?? '—'}</span>
                    {a.command && <span className="k-mono k-evidence__cmd">{a.command}</span>}
                    {a.changedPaths && a.changedPaths.length > 0 && (
                      <span className="k-evidence__files">
                        {t('work.evidence.changedFiles', { count: a.changedPaths.length })}
                      </span>
                    )}
                    {a.superseded && <span className="k-evidence__old">{t('work.evidence.superseded')}</span>}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </QueryState>
    </div>
  )
}

/* ----------------------------------------------------------------- Dialogs */

function splitLines(s: string): string[] | undefined {
  const lines = s
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
  return lines.length > 0 ? lines : undefined
}

function CreateMissionDialog({
  projects,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  projects: ProjectLite[]
  busy: boolean
  error: unknown
  onClose: () => void
  onSubmit: (input: {
    projectId: ID
    title: string
    objective: string
    priority: number
    riskLevel: string
    acceptanceCriteria?: string[]
  }) => void
}) {
  const t = useT()
  const describe = useApiErrorMessage()
  const [projectId, setProjectId] = useState<ID>(projects.length === 1 ? projects[0]!.id : '')
  const [title, setTitle] = useState('')
  const [objective, setObjective] = useState('')
  const [priority, setPriority] = useState('50')
  const [risk, setRisk] = useState<string>('L2_VALIDATED_CHANGE')
  const [acceptance, setAcceptance] = useState('')
  const can = projectId !== '' && title.trim() !== '' && objective.trim() !== '' && !busy
  return (
    <Dialog
      title={t('work.createMission')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!can}
            busy={busy}
            onClick={() =>
              onSubmit({
                projectId,
                title: title.trim(),
                objective: objective.trim(),
                priority: Number(priority) || 0,
                riskLevel: risk,
                acceptanceCriteria: splitLines(acceptance),
              })
            }
          >
            {t('action.create')}
          </Button>
        </>
      }
    >
      <div className="k-form">
        <SelectField
          label={t('work.mission.project')}
          value={projectId}
          onChange={setProjectId}
          options={[{ value: '', label: '—' }, ...projects.map((p) => ({ value: p.id, label: p.name }))]}
        />
        <TextField label={t('work.mission.title')} value={title} onChange={setTitle} required />
        <TextAreaField label={t('work.mission.objective')} value={objective} onChange={setObjective} rows={3} required />
        <TextField label={t('work.mission.priority')} value={priority} onChange={setPriority} inputMode="numeric" mono />
        <SelectField
          label={t('work.mission.risk')}
          value={risk}
          onChange={setRisk}
          options={RISKS.map((r) => ({ value: r, label: r }))}
        />
        <TextAreaField label={t('work.mission.acceptance')} value={acceptance} onChange={setAcceptance} rows={3} />
        {error != null && <Notice tone="err">{describe(error).title}</Notice>}
      </div>
    </Dialog>
  )
}

function CreateTaskDialog({
  mission,
  siblings,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  mission: MissionResponse
  siblings: TaskResponse[]
  busy: boolean
  error: unknown
  onClose: () => void
  onSubmit: (input: {
    title: string
    objective: string
    archetype: string
    priority: number
    acceptanceCriteria?: string[]
    dependsOn?: ID[]
  }) => void
}) {
  const t = useT()
  const describe = useApiErrorMessage()
  const [title, setTitle] = useState('')
  const [objective, setObjective] = useState('')
  const [archetype, setArchetype] = useState<string>('backend')
  const [priority, setPriority] = useState(String(mission.priority))
  const [acceptance, setAcceptance] = useState('')
  const [dependsOn, setDependsOn] = useState<ID>('')
  const can = title.trim() !== '' && objective.trim() !== '' && !busy
  return (
    <Dialog
      title={`${t('work.createTask')} · ${mission.title}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!can}
            busy={busy}
            onClick={() =>
              onSubmit({
                title: title.trim(),
                objective: objective.trim(),
                archetype,
                priority: Number(priority) || 0,
                acceptanceCriteria: splitLines(acceptance),
                dependsOn: dependsOn ? [dependsOn] : undefined,
              })
            }
          >
            {t('action.create')}
          </Button>
        </>
      }
    >
      <div className="k-form">
        <TextField label={t('work.task.title')} value={title} onChange={setTitle} required />
        <TextAreaField label={t('work.task.objective')} value={objective} onChange={setObjective} rows={3} required />
        <SelectField
          label={t('work.task.archetype')}
          value={archetype}
          onChange={setArchetype}
          options={ARCHETYPES.map((a) => ({ value: a, label: labelFor(archetypeKey(a), a, t) }))}
        />
        <TextField label={t('work.task.priority')} value={priority} onChange={setPriority} inputMode="numeric" mono />
        <TextAreaField label={t('work.task.acceptance')} value={acceptance} onChange={setAcceptance} rows={3} />
        {siblings.length > 0 && (
          <SelectField
            label={t('work.task.dependsOn')}
            value={dependsOn}
            onChange={setDependsOn}
            options={[{ value: '', label: '—' }, ...siblings.map((s) => ({ value: s.id, label: s.title }))]}
          />
        )}
        {error != null && <Notice tone="err">{describe(error).title}</Notice>}
      </div>
    </Dialog>
  )
}

function DecisionDialog({
  task,
  decision,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  task: TaskResponse
  decision: TaskDecision
  busy: boolean
  error: unknown
  onClose: () => void
  onSubmit: (reason: string) => void
}) {
  const t = useT()
  const describe = useApiErrorMessage()
  const [reason, setReason] = useState('')
  return (
    <Dialog
      title={`${t(decisionKey(decision))} · ${task.title}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button variant={decision === 'cancel' ? 'danger' : 'primary'} busy={busy} onClick={() => onSubmit(reason.trim())}>
            {t(decisionKey(decision))}
          </Button>
        </>
      }
    >
      <div className="k-form">
        <p>{t('work.decision.confirm', { decision: t(decisionKey(decision)) })}</p>
        <TextAreaField label={t('work.decision.reason')} value={reason} onChange={setReason} rows={3} />
        {error != null && <Notice tone="err">{describe(error).title}</Notice>}
      </div>
    </Dialog>
  )
}
